import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import AddExperienceModal from '../Experience/AddExperienceModal';
import { useUserContext } from '../../contexts/UserContext';
import { getContentfulEntries } from '../../utils/APIs';
import ErrorAlert from '../UI/Alert/ErrorAlert';
import { formatDate } from '../../utils/converter';
import { toast } from 'react-toastify';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { getAgGridSortStorageKey } from '../UI/AgGrid';
import { Card, Button, Input, AgTableCard } from '../UI';

const ManageContentfulEntries = () => {
    const [rowData, setRowData] = useState([]);
    const [showAddExperiencesModal, setShowAddExperiencesModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [gridApi, setGridApi] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const { userData } = useUserContext();
    const { enums, content } = useContentfulContext();
    const navigator = useNavigate();

    useEffect(() => {
        if (userData?.defaultContentful) {
            getActivityData();
        }
    }, [userData?.defaultContentful?._id]);

    const getActivityData = async () => {
        try {
            if (!userData?.defaultContentful) {
                toast.error(content.LIST_CONTENTFUL_ENTRIES_CONTENT.error.no_contentful_found);
                return;
            }
            setLoading(true);
            const result = await getContentfulEntries({
                token: userData?.token,
                organizationId: userData?.selectedOrganization?.org_token,
                contentful_id: userData?.defaultContentful?._id,
            });

            setError("");
            setRowData(result.data?.items || []);
        } catch (err) {
            setError(err?.message || content?.COMMON_MESSAGE.something_wrong_msg);
            toast.error(err?.message || content?.COMMON_MESSAGE.something_wrong_msg);
        } finally {
            setLoading(false);
        }
    };

    const onGridReady = (params) => {
        setGridApi(params.api);

        const storageKey = getAgGridSortStorageKey("manage-contentful-entries-table");
        const sortModel = JSON.parse(localStorage.getItem(storageKey));

        if (!sortModel || sortModel?.length === 0) {
            params.columnApi.applyColumnState({
                state: [
                    { colId: 'sys.updatedAt', sort: 'desc' }
                ],
                applyOrder: false,
            });
        }
    };

    const autoSizeStrategy = useMemo(() => {
        return {
            type: "fitGridWidth",
            defaultMinWidth: 40,
            columnLimits: [
                {
                    colId: "sys.id",
                    minWidth: 260,
                },
                {
                    colId: "sys.contentType.sys.id",
                    minWidth: 200,
                },
                {
                    colId: "sys.publishedVersion",
                    minWidth: 160,
                },
                {
                    colId: "sys.createdAt",
                    minWidth: 180,
                },
                {
                    colId: "sys.updatedAt",
                    minWidth: 180,
                },
            ],
        };
    }, []);

    const handleCloseModal = () => {
        setShowAddExperiencesModal(false);
    };

    const onFilterTextBoxChanged = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        if (gridApi) {
            gridApi.setFilterModel({
                ...gridApi.getFilterModel(),
                "sys.id": value ? { type: 'contains', filter: value.toLowerCase() } : null
            });
        }
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        if (gridApi) {
            const currentFilter = gridApi.getFilterModel();
            delete currentFilter["sys.id"];
            gridApi.setFilterModel(currentFilter);
        }
    };

    const colDefs = [
        {
            headerName: content.LIST_CONTENTFUL_ENTRIES_CONTENT.table_headers.title,
            field: "sys.id",
            cellRenderer: ({ data }) => (
                <Link
                    className="font-medium text-primary hover:text-primary-hover hover:underline"
                    to={`/experience/contentful-entries/${data.sys.id}`}
                >
                    {data.sys.id}
                </Link>
            ),
            filter: true,
            suppressMovable: true
        },
        {
            headerName: "Content Type",
            field: "sys.contentType.sys.id",
            suppressMovable: true,
            sortable: true,
            cellRenderer: ({ value }) => (
                <span className="stride-badge-pill bg-slate-100 text-slate-700 font-mono text-[11px]">
                    {value || '—'}
                </span>
            )
        },
        {
            headerName: "Published Version",
            field: "sys.publishedVersion",
            suppressMovable: true,
            sortable: true,
            cellRenderer: ({ value }) => (
                <span className="text-stride-body text-sm font-medium">
                    v{value || 1}
                </span>
            )
        },
        {
            headerName: content.LIST_CONTENTFUL_ENTRIES_CONTENT.table_headers.created_date,
            field: "sys.createdAt",
            cellRenderer: ({ data }) => (
                <span className="text-stride-muted text-xs">
                    {formatDate(data.sys.createdAt)}
                </span>
            ),
            comparator: (date1, date2) => new Date(date1) - new Date(date2),
            suppressMovable: true,
            sortable: true
        },
        {
            headerName: content.LIST_CONTENTFUL_ENTRIES_CONTENT.table_headers.last_modified_date,
            field: "sys.updatedAt",
            cellRenderer: ({ data }) => (
                <span className="text-stride-muted text-xs">
                    {formatDate(data.sys.updatedAt)}
                </span>
            ),
            comparator: (date1, date2) => new Date(date1) - new Date(date2),
            suppressMovable: true,
            sortable: true
        },
    ];

    const hasIntegration = Boolean(userData?.defaultContentful || userData?.defaultAdobeIO);

    return (
        <>
            {error && <ErrorAlert timeout={content.LIST_CONTENTFUL_ENTRIES_CONTENT.timeout}>{error}</ErrorAlert>}
            <main id="main" className="stride-page-shell flex flex-col space-y-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <span className="stride-eyebrow">Experiences / Manage</span>
                        <h1 className="text-2xl font-bold tracking-tight text-stride-heading">
                            {content.LIST_CONTENTFUL_ENTRIES_CONTENT.title}
                        </h1>
                        <p className="text-sm text-stride-muted mt-1">
                            {rowData?.length || 0} total Contentful entries synced
                        </p>
                    </div>
                </div>

                {/* Empty State */}
                {!hasIntegration && (
                    <Card className="text-center p-12 max-w-lg mx-auto">
                        <p className="text-base text-stride-heading font-medium mb-4">
                            {content.LIST_CONTENTFUL_ENTRIES_CONTENT.no_adobe_io}
                        </p>
                        <Button
                            variant="primary"
                            onClick={() => navigator('/setup/contentful-integrations')}
                        >
                            Configure Contentful Integration
                        </Button>
                    </Card>
                )}

                <AddExperienceModal
                    showAddExperiencesModal={showAddExperiencesModal}
                    handleCloseModal={handleCloseModal}
                />

                {/* Quartz AG-Grid Table inside Card with Search in Header */}
                {hasIntegration && (
                    <AgTableCard
                        header={
                            <div className="w-full md:w-80">
                                <Input
                                    placeholder={content?.LIST_CONTENTFUL_ENTRIES_CONTENT?.search_placeholder || "Search by ID..."}
                                    value={searchTerm}
                                    onChange={onFilterTextBoxChanged}
                                    prefixIcon={<Search className="w-4 h-4 text-stride-muted" />}
                                    clearable
                                    onClear={handleClearSearch}
                                />
                            </div>
                        }
                        tableId="manage-contentful-entries-table"
                        loading={loading}
                        rowData={rowData}
                        columnDefs={colDefs}
                        autoSizeStrategy={autoSizeStrategy}
                        onGridReady={onGridReady}
                        rowSelection="multiple"
                        suppressRowClickSelection
                        pagination={true}
                        paginationPageSize={content.LIST_CONTENTFUL_ENTRIES_CONTENT.default_page_size}
                        paginationPageSizeSelector={enums?.PAGINATION_PAGE_SIZE_SELECTOR}
                    />
                )}
            </main>
        </>
    );
};

export default ManageContentfulEntries;
